import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { TrackingEvent } from './tracking-events';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({ providedIn: 'root' })
export class InsightsService {
  private router = inject(Router);

  private angularPlugin = new AngularPlugin();
  private appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: this.getInstrumentationKey(),
      endpointUrl: TreetopPropertiesProvider.properties.insights.endpoint,
      extensions: [this.angularPlugin],
      extensionConfig: {
        [this.angularPlugin.identifier]: {
          router: this.router,
          errorServices: [new ErrorHandler()],
        },
      },
    },
  });

  private getInstrumentationKey(): string | null {
    if (!TreetopPropertiesProvider.properties.insights.connectionString) {
      return null;
    }

    const parts = TreetopPropertiesProvider.properties.insights.connectionString.split(';');
    const instrumentationKeyPart = parts.find((part) => part.startsWith('InstrumentationKey='));

    if (!instrumentationKeyPart) {
      return null;
    }

    return instrumentationKeyPart.replace('InstrumentationKey=', '');
  }

  constructor() {
    if (TreetopPropertiesProvider.properties.insights.connectionString !== null) {
      this.init();
    }
  }

  private init(): void {
    try {
      this.appInsights.loadAppInsights();
    } catch (_) {
      // No need to do anything here as the service will simply not send any telemetry if initialization fails
    }
  }

  public trackEvent<T extends TrackingEvent>(event: T, properties?: Record<string, unknown>): void {
    if (TreetopPropertiesProvider.properties.insights.connectionString === null) {
      return;
    }

    this.appInsights.trackEvent({
      name: event,
      properties: {
        ...properties,
        timestamp: properties?.timestamp || new Date().toISOString(),
      },
    });
  }

  public trackTrace(message: string): void {
    this.appInsights.trackTrace({ message });
  }

  public trackError(message: string): void {
    if (TreetopPropertiesProvider.properties.insights.connectionString === null) {
      return;
    }

    this.appInsights.trackException({ exception: new Error(message) });
  }

  public setAuthenticatedUserContext(organisationId: string): void {
    //due to gdpr we should not send the real user id
    this.appInsights.setAuthenticatedUserContext(EMPTY_GUID, organisationId);
  }
}
